<template>
  <div 
    v-if="isVisible" 
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
    @click.self="handleBackdropClick"
  >
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-4">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Preparing Your Download
        </h3>
        <button 
          @click="handleSkip"
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          :disabled="countdown > 0"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Ad Container -->
      <div class="mb-6">
        <GoogleAd 
          ad-type="square" 
          :show-label="false"
          :min-content-height="0"
          class="min-h-[200px] flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg"
        />
      </div>

      <!-- Countdown and Actions -->
      <div class="text-center">
        <div v-if="countdown > 0" class="mb-4">
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Your download will start in {{ countdown }} seconds
          </p>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div 
              class="bg-blue-600 h-2 rounded-full transition-all duration-1000"
              :style="{ width: `${((initialCountdown - countdown) / initialCountdown) * 100}%` }"
            ></div>
          </div>
        </div>

        <div v-else class="mb-4">
          <p class="text-sm text-green-600 dark:text-green-400 mb-2">
            Ready to download!
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 justify-center">
          <button
            v-if="countdown === 0"
            @click="handleDownload"
            class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Download Now
          </button>
          
          <button
            @click="handleSkip"
            :disabled="countdown > 3"
            :class="[
              'px-4 py-2 rounded-lg transition-colors text-sm',
              countdown > 3
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
            ]"
          >
            {{ countdown > 3 ? `Skip (${countdown - 3}s)` : 'Skip Ad' }}
          </button>
        </div>
      </div>

      <!-- Download Info -->
      <div v-if="downloadInfo" class="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div class="flex items-center justify-between text-sm">
          <span class="text-gray-600 dark:text-gray-400">File:</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ downloadInfo.name }}</span>
        </div>
        <div class="flex items-center justify-between text-sm mt-1">
          <span class="text-gray-600 dark:text-gray-400">Size:</span>
          <span class="font-medium text-gray-900 dark:text-white">{{ downloadInfo.size }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import GoogleAd from './GoogleAd.vue';

interface Props {
  isVisible: boolean;
  downloadInfo?: {
    name: string;
    size: string;
  };
  countdownDuration?: number;
}

interface Emits {
  (e: 'download'): void;
  (e: 'close'): void;
  (e: 'skip'): void;
}

const props = withDefaults(defineProps<Props>(), {
  countdownDuration: 5
});

const emit = defineEmits<Emits>();

// State
const countdown = ref(0);
const initialCountdown = ref(0);
let countdownInterval: number | null = null;

// Methods
const startCountdown = () => {
  countdown.value = props.countdownDuration;
  initialCountdown.value = props.countdownDuration;
  
  countdownInterval = setInterval(() => {
    countdown.value--;
    
    if (countdown.value <= 0) {
      clearInterval(countdownInterval!);
      countdownInterval = null;
    }
  }, 1000);
};

const handleDownload = () => {
  emit('download');
  handleClose();
};

const handleSkip = () => {
  if (countdown.value > 3) return; // Prevent skipping too early
  
  emit('skip');
  handleClose();
};

const handleBackdropClick = () => {
  if (countdown.value <= 3) {
    handleSkip();
  }
};

const handleClose = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  countdown.value = 0;
  emit('close');
};

// Lifecycle
onMounted(() => {
  if (props.isVisible) {
    startCountdown();
  }
});

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});

// Watch for visibility changes
import { watch } from 'vue';
watch(() => props.isVisible, (newValue) => {
  if (newValue) {
    startCountdown();
  } else {
    handleClose();
  }
});
</script>

<style scoped>
/* Ensure modal appears above everything */
.fixed {
  z-index: 9999;
}

/* Smooth transitions */
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
</style>